import React from 'react';
import { getPickupLocation, getEstimatedDeliveryDate } from '../utils/deliveryUtils';
import { formatIdrFromUsd } from '../utils/currency';

const DeliveryOption = ({ 
  option, 
  isSelected, 
  onSelect, 
  disabled = false 
}) => {
  const { id, name, description, cost, estimatedDays, icon } = option;
  
  const handleSelect = () => {
    if (!disabled && onSelect) {
      onSelect(id);
    }
  };

  const estimatedDate = getEstimatedDeliveryDate(id);
  const pickupLocation = id === 'PICKUP' ? getPickupLocation() : null;

  return (
    <div 
      className={`
        border rounded-lg p-6 cursor-pointer transition-all duration-200
        ${isSelected 
          ? 'border-primary bg-primary/5 shadow-lg' 
          : 'border-gray-700 hover:border-gray-600 bg-secondary'
        }
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-md'}
      `}
      onClick={handleSelect}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center mb-2">
            <span className="text-2xl mr-3">{icon}</span>
            <div>
              <h3 className={`font-semibold text-lg ${isSelected ? 'text-primary' : 'text-light'}`}>
                {name}
              </h3>
              <p className="text-gray-400 text-sm">
                {description}
              </p>
            </div>
          </div>

          <div className="ml-11 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-gray-400">Cost:</span>
              <span className={`font-semibold ${isSelected ? 'text-primary' : 'text-light'}`}>
                {cost === 0 ? 'Free' : formatIdrFromUsd(cost)}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-gray-400">Estimated Delivery:</span>
              <span className="text-light text-sm">
                {id === 'PICKUP' ? estimatedDays : estimatedDate}
              </span>
            </div>

            {/* Special information for pickup */}
            {id === 'PICKUP' && pickupLocation && (
              <div className="mt-4 p-3 bg-primary/10 rounded-md border border-primary/20">
                <div className="text-sm">
                  <p className="text-primary font-semibold mb-1">
                    🏪 {pickupLocation.name}
                  </p>
                  <p className="text-gray-300 text-xs mb-2">
                    {pickupLocation.formattedAddress}
                  </p>
                  <div className="grid grid-cols-2 gap-2 text-xs text-gray-400">
                    <div>
                      <span className="font-medium">Hours:</span>
                      <br />Mon-Sat: 9AM-6PM
                      <br />Sunday: 10AM-4PM
                    </div>
                    <div>
                      <span className="font-medium">Contact:</span>
                      <br />{pickupLocation.contact.phone}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Tracking information for shipping options */}
            {id !== 'PICKUP' && (
              <div className="mt-3 text-xs text-gray-400">
                ✓ Tracking number provided
                {id === 'DHL' && <span> • International shipping</span>}
                {id === 'LION_PARCEL' && <span> • Indonesia-wide delivery</span>}
              </div>
            )}
          </div>
        </div>

        {/* Selection indicator */}
        <div className={`
          w-6 h-6 rounded-full border-2 flex items-center justify-center ml-4
          ${isSelected 
            ? 'border-primary bg-primary' 
            : 'border-gray-600'
          }
        `}>
          {isSelected && (
            <div className="w-3 h-3 rounded-full bg-dark"></div>
          )}
        </div>
      </div>

      {/* Outlaw Souls messaging for special delivery methods */}
      {id === 'PICKUP' && (
        <div className="mt-4 pt-3 border-t border-gray-800">
          <p className="text-xs text-primary italic">
            "Visit our workshop where rebellion takes form. Meet SASTRAJIWA and witness the craft."
          </p>
        </div>
      )}

      {id === 'DHL' && (
        <div className="mt-4 pt-3 border-t border-gray-800">
          <p className="text-xs text-primary italic">
            "Your artifact of rebellion travels across borders. Wear it with pride, outlaw."
          </p>
        </div>
      )}
    </div>
  );
};

export default DeliveryOption;
